import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import { auditKeys } from './queries';

/**
 * Slice 19c — Form notes drawer API: the notes auditors wrote on the form,
 * per report section, and whether each one is in the report.
 */

export type NoteSourceStatus =
  | 'NoNote'
  | 'NotUsed'
  | 'AwaitingReview'
  | 'Added'
  | 'Changed'
  | 'Removed'
  | 'NoLongerApplies';

export type NoteInsertion = {
  insertionId: string;
  state: 'PendingReview' | 'Kept' | 'Removed';
  sectionKey: string;
  sourceTextAtInsert: string;
  insertedAtUtc: string;
  insertedByUserId: string;
  insertedByDisplayName: string | null;
};

export type NoteSource = {
  criterionItemId: string;
  criterionCode: string;
  criterionName: string;
  /** 1-based position of the item within its criterion. */
  itemNumber: number;
  itemText: string;
  isApplicable: boolean;
  /** The note on the form now; null when there is none. */
  note: string | null;
  status: NoteSourceStatus;
  /** The note differs from what was inserted (drives "Replace with current version"). */
  sourceChangedSinceInsert: boolean;
  latestInsertion: NoteInsertion | null;
};

export type SectionNoteSources = {
  sectionKey: string;
  sectionCode: string;
  sectionName: string;
  items: NoteSource[];
};

/** "3.2.1": criterion code plus the item's position. */
export function noteLabel(source: Pick<NoteSource, 'criterionCode' | 'itemNumber'>): string {
  return `${source.criterionCode}.${source.itemNumber}`;
}

/** True for the per-template-section narratives, the only ones with form notes. */
export function hasFormNotes(sectionKey: string): boolean {
  return sectionKey.startsWith('section_narrative_');
}

export const formNoteKeys = {
  all: (tenantId: string, auditId: string) =>
    ['audits', tenantId, auditId, 'form-notes'] as const,
  section: (tenantId: string, auditId: string, sectionKey: string) =>
    ['audits', tenantId, auditId, 'form-notes', 'section', sectionKey] as const,
  attention: (tenantId: string, auditId: string) =>
    ['audits', tenantId, auditId, 'form-notes', 'attention'] as const,
};

export function useFormNoteSources(tenantId: string, auditId: string, sectionKey: string, enabled: boolean) {
  return useQuery({
    queryKey: formNoteKeys.section(tenantId, auditId, sectionKey),
    queryFn: () => api<SectionNoteSources>(
      `/api/audit/audits/${auditId}/report/sections/${sectionKey}/note-sources`),
    enabled: enabled && hasFormNotes(sectionKey),
    // Notes are edited on the Form tab; pick changes up on return.
    refetchOnWindowFocus: true,
    staleTime: 0,
  });
}

/** Section key → items needing attention (awaiting review, or changed since kept). */
export function useNoteAttention(tenantId: string, auditId: string) {
  return useQuery({
    queryKey: formNoteKeys.attention(tenantId, auditId),
    queryFn: async () =>
      (await api<{ sections: Record<string, number> }>(
        `/api/audit/audits/${auditId}/report/note-attention`)).sections,
    refetchOnWindowFocus: true,
    staleTime: 0,
  });
}

function useInvalidateFormNotes(tenantId: string, auditId: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: formNoteKeys.all(tenantId, auditId) });
    void qc.invalidateQueries({ queryKey: auditKeys.activity(tenantId, auditId) });
  };
}

export function useInsertFormNote(tenantId: string, auditId: string) {
  const invalidate = useInvalidateFormNotes(tenantId, auditId);
  return useMutation({
    mutationFn: (input: { sectionKey: string; criterionItemId: string }) =>
      api<{ insertionId: string; contentJson: string }>(
        `/api/audit/audits/${auditId}/report/sections/${input.sectionKey}/note-insertions`,
        { method: 'POST', body: { criterionItemId: input.criterionItemId } }),
    onSettled: invalidate,
  });
}

export function useKeepInsertion(tenantId: string, auditId: string) {
  const invalidate = useInvalidateFormNotes(tenantId, auditId);
  return useMutation({
    mutationFn: (insertionId: string) =>
      api<void>(`/api/audit/audits/${auditId}/report/note-insertions/${insertionId}/keep`,
        { method: 'POST' }),
    onSettled: invalidate,
  });
}

export function useRemoveInsertion(tenantId: string, auditId: string) {
  const invalidate = useInvalidateFormNotes(tenantId, auditId);
  return useMutation({
    mutationFn: (insertionId: string) =>
      api<void>(`/api/audit/audits/${auditId}/report/note-insertions/${insertionId}/remove`,
        { method: 'POST' }),
    onSettled: invalidate,
  });
}

export function useDismissNoteSource(tenantId: string, auditId: string) {
  const invalidate = useInvalidateFormNotes(tenantId, auditId);
  return useMutation({
    mutationFn: (criterionItemId: string) =>
      api<void>(`/api/audit/audits/${auditId}/report/note-sources/${criterionItemId}/dismiss`,
        { method: 'POST' }),
    onSettled: invalidate,
  });
}

/**
 * The drawer's open/closed state, remembered per browser. Closed by default
 * so the Report tab stays uncluttered until someone asks for it.
 */
const DRAWER_KEY = 'dpas.report.formNotesDrawer.open';

export function readDrawerOpen(): boolean {
  try {
    return window.localStorage.getItem(DRAWER_KEY) === 'true';
  } catch {
    return false;
  }
}

export function writeDrawerOpen(open: boolean): void {
  try {
    window.localStorage.setItem(DRAWER_KEY, open ? 'true' : 'false');
  } catch {
    // Private mode or blocked storage: the drawer just won't be remembered.
  }
}

/** The body of the 409 a publish returns while form-note blocks are pending. */
export type PendingNoteInsertionsProblem = {
  type: string;
  title: string;
  detail: string;
  pending: {
    insertionId: string;
    sectionKey: string;
    criterionCode: string;
    criterionTitle: string;
    itemText: string;
  }[];
};

export const PENDING_NOTE_INSERTIONS_PROBLEM = 'https://dpas.app/problems/pending-note-insertions';

export function asPendingNoteInsertionsProblem(err: unknown): PendingNoteInsertionsProblem | null {
  const body = (err as { status?: number; body?: unknown })?.body as Partial<PendingNoteInsertionsProblem> | undefined;
  return body && body.type === PENDING_NOTE_INSERTIONS_PROBLEM && Array.isArray(body.pending)
    ? (body as PendingNoteInsertionsProblem)
    : null;
}
